import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'

interface SecConditionsProps {
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	background?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string
					text: string
				}
			}[]
		}
	}[]
	button?: {
		href: string
		label: string
	}
}

export const fields = [
	// headings
	'heading.title',

	// text
	'text.text',

	// image
	'background.alt',
	'background.image_file.image.id',
	'background.image_file.image.filename_download',

	// content collection
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',

	// button
	'button.href',
	'button.label',
] as const

export default function SecConditions({ data }: { data: SecConditionsProps }) {

	return (
		<section
			id='conditions'
			className='relative w-full pb-12 md:pb-20 overflow-hidden md:bg-transparent bg-[#60676A]'
		>
			{/* Background Image */}
			<div className='absolute inset-0 hidden md:block'>
				{data?.background?.image_file?.image?.id && (
					<Image
						src={getReadableImageUrl(
							data.background.image_file.image.id,
							data.background.image_file.image.filename_disk || ''
						)}
						alt={data.background.alt || ''}
						fill
						className='object-cover object-center rounded-tl-[4.125rem]'
						priority
					/>
				)}
			</div>

			{/* Content Container */}
			<div className='container relative z-10'>
				{/* Header Section */}
				<div className='relative'>
					<div className='absolute right-full top-0 w-screen h-full bg-[#929986]' />

					<div className='relative w-full md:w-1/2 bg-[#929986] py-12 md:pr-12 xl:pr-20 xl:py-20 md:rounded-tr-lg md:rounded-br-lg'>
						<h2 className='text-white mb-9'>{data?.heading?.title || ''}</h2>

						<div
							className='text-white'
							dangerouslySetInnerHTML={sanitizeHTML(
								(data?.text?.text || '').replace(
									/<p>/g,
									'<p class="xl:text-2xl mb-6">'
								)
							)}
						></div>
					</div>

					<div className='absolute left-full top-0 w-screen h-full bg-[#929986] md:hidden' />
				</div>
			</div>

			{/* Mobile Image */}
			<div className='md:hidden w-full'>
				{data?.background?.image_file?.image?.id && (
					<Image
						src={getReadableImageUrl(
							data.background.image_file.image.id,
							data.background.image_file.image.filename_disk || ''
						)}
						alt={data.background.alt || ''}
						width={768}
						height={400}
						className='w-full h-auto'
						priority
					/>
				)}
			</div>

			<div className='container relative z-10'>
				{/* Conditions Grid */}
				<div
					className={`ConditionsGrid gap-8 mt-12 mb-12 ${
						(() => {
							const itemCount = (data?.content_collection || []).length;
							
							// 2 or fewer - regular 6 column grid
							if (itemCount <= 2) {
								if (itemCount === 1) {
									return 'grid md:gap-1 md:grid-cols-2 2xl:grid-cols-2';
								} else {
									return 'grid md:grid-cols-3 2xl:grid-cols-6';
								}
							}
							
							// 3-4 elements - stretch to full width
							if (itemCount >= 3 && itemCount <= 4) {
								return 'grid md:grid-cols-3 2xl:grid-cols-6 lg:flex';
							}
							
							// 5-6 elements - regular grid
							if (itemCount >= 5 && itemCount <= 6) {
								return 'grid md:grid-cols-3 2xl:grid-cols-6';
							}
							
							// 7+ elements - special layout
							return 'grid md:grid-cols-3 2xl:grid-cols-6';
						})()
					}`}
				>
					{(() => {
						const items = data?.content_collection || [];
						const itemCount = items.length;
						
						// Special layout only for 7+ elements
						if (itemCount >= 7) {
							const firstRowCount = Math.ceil(itemCount / 2);
							const secondRowCount = itemCount - firstRowCount;
							
							return (
								<>
									{/* Mobile version - regular list */}
									<div className="2xl:hidden contents">
										{items.map((item, index) => (
											<div
												key={`condition-mobile-${item.id}-${index}`}
												className='flex-1 px-6 py-7 bg-black/30 backdrop-blur-[13.30px] rounded-lg max-md:grid max-md:grid-cols-[2.4rem_1fr] max-md:gap-3'
											>
												<div className='flex items-center gap-3 md:mb-6'>
													<div className='size-9 bg-[#7f8674] rounded-tl-md rounded-br-md flex items-center justify-center max-md:h-full'>
														<span className='text-white text-2xl uppercase'>
															{index + 1}
														</span>
													</div>
												</div>
												<div
													className='text-white text-xl'
													dangerouslySetInnerHTML={sanitizeHTML(
														item?.site_content_collections_id?.collection?.[0]?.item
															?.title ||
															item?.site_content_collections_id?.collection?.[0]?.item
																?.text ||
															''
													)}
												></div>
											</div>
										))}
									</div>

									{/* Desktop version - special layout for 7+ elements */}
									<div className="hidden 2xl:contents">
										{/* First row */}
										<div className="col-span-full grid grid-cols-6 gap-8">
											{items.slice(0, firstRowCount).map((item, index) => (
												<div
													key={`condition-row1-${item.id}-${index}`}
													className='flex-1 px-6 py-7 bg-black/30 backdrop-blur-[13.30px] rounded-lg col-span-1'
												>
													<div className='flex items-center gap-3 mb-6'>
														<div className='size-9 bg-[#7f8674] rounded-tl-md rounded-br-md flex items-center justify-center'>
															<span className='text-white text-2xl uppercase'>
																{index + 1}
															</span>
														</div>
													</div>
													<div
														className='text-white text-xl'
														dangerouslySetInnerHTML={sanitizeHTML(
															item?.site_content_collections_id?.collection?.[0]?.item
																?.title ||
																item?.site_content_collections_id?.collection?.[0]?.item
																	?.text ||
																''
														)}
													></div>
												</div>
											))}
										</div>
										
										{/* Second row */}
										<div className="col-span-full grid grid-cols-6 gap-8">
											{items.slice(firstRowCount).map((item, index) => (
												<div
													key={`condition-row2-${item.id}-${index}`}
													className='flex-1 px-6 py-7 bg-black/30 backdrop-blur-[13.30px] rounded-lg col-span-1'
												>
													<div className='flex items-center gap-3 mb-6'>
														<div className='size-9 bg-[#7f8674] rounded-tl-md rounded-br-md flex items-center justify-center'>
															<span className='text-white text-2xl uppercase'>
																{firstRowCount + index + 1}
															</span>
														</div>
													</div>
													<div
														className='text-white text-xl'
														dangerouslySetInnerHTML={sanitizeHTML(
															item?.site_content_collections_id?.collection?.[0]?.item
																?.title ||
																item?.site_content_collections_id?.collection?.[0]?.item
																	?.text ||
																''
														)}
													></div>
												</div>
											))}
										</div>
									</div>
								</>
							);
						}
						
						// Regular layout for 3-6 elements and 2 or fewer
						return items.map((item, index) => (
							<div
								key={`condition-${item.id}-${index}`}
								className='flex-1 px-6 py-7 bg-black/30 backdrop-blur-[13.30px] rounded-lg max-md:grid max-md:grid-cols-[2.4rem_1fr] max-md:gap-3'
							>
								<div className='flex items-center gap-3 md:mb-6'>
									<div className='size-9 bg-[#7f8674] rounded-tl-md rounded-br-md flex items-center justify-center max-md:h-full'>
										<span className='text-white text-2xl uppercase'>
											{index + 1}
										</span>
									</div>
								</div>
								<div
									className='text-white text-xl'
									dangerouslySetInnerHTML={sanitizeHTML(
										item?.site_content_collections_id?.collection?.[0]?.item
											?.title ||
											item?.site_content_collections_id?.collection?.[0]?.item
												?.text ||
											''
									)}
								></div>
							</div>
						));
					})()}
				</div>

				{/* CTA Button */}
				{data?.button && (
					<div className='flex justify-end'>
						<Link
							href={data.button?.href || '/'}
							className='btn'
							onClick={e => {
								if (data.button?.href) {
									e.preventDefault()
									window.location.href = data.button.href
								}
							}}
						>{data.button.label}
						</Link>
					</div>
				)}
			</div>
		</section>
	)
}
